import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const dateFormat = "DD/MM/YYYY";

export const MEDAL_COLORS = {
    gold: "var(--color-medal-gold)",
    silver: "var(--color-medal-silver)",
    bronze: "var(--color-medal-bronze)",
} as const;

export const KPI_TIERS = [
    { from: 0, to: 40, color: "#ef4444", label: "Weakest", medal: null },
    { from: 40, to: 50, color: "#f97316", label: "Weaker", medal: null },
    { from: 50, to: 60, color: "#eab308", label: "Average", medal: null },
    { from: 60, to: 70, color: "#84cc16", label: "Solid", medal: null },
    { from: 70, to: 80, color: "#22c55e", label: "Very Good", medal: "bronze" as const },
    { from: 80, to: 90, color: "#06b6d4", label: "Excellent", medal: "silver" as const },
    { from: 90, to: Infinity, color: "#3b82f6", label: "World Class", medal: "gold" as const },
] as const;

export function getPointsBadge(value: number): { bg: string; text: string; border: string } {
    if (value >= 90) return { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-100" };
    if (value >= 80) return { bg: "bg-cyan-50", text: "text-cyan-700", border: "border-cyan-100" };
    if (value >= 70) return { bg: "bg-green-50", text: "text-green-700", border: "border-green-100" };
    if (value >= 60) return { bg: "bg-lime-50", text: "text-lime-700", border: "border-lime-100" };
    if (value >= 50) return { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-100" };
    if (value >= 40) return { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-100" };
    return { bg: "bg-red-100", text: "text-red-700", border: "border-red-200" };
}

export function getKpiTier(kpi: number): { color: string; label: string; medal: "bronze" | "silver" | "gold" | null } {
    if (kpi === 0) return { color: "#CFCFCF", label: "Insufficient Data", medal: null };
    return KPI_TIERS.find((t) => kpi >= t.from && kpi < t.to) ?? KPI_TIERS[0];
}

const TEAM_LOGOS: Record<string, string> = {
    frisk: "/team-logos/Frisk-Asker-small.png",
    grüner: "/team-logos/Gruner-small.png",
    lillehammer: "/team-logos/Lillehammer-small.png",
    lørenskog: "/team-logos/Lorenskog-small.png",
    manglerud: "/team-logos/Manglerud-Star-small.png",
    narvik: "/team-logos/Narvik-Hockey-Dark-small.png",
    nidaros: "/team-logos/Nidaros-small.png",
    ringerike: "/team-logos/Ringerike-small.png",
    sparta: "/team-logos/Sparta-Sarpsborg-small.png",
    sarpsborg: "/team-logos/Sparta-Sarpsborg-small.png",
    stavanger: "/team-logos/Stavanger-Oilers-small.png",
    oilers: "/team-logos/Stavanger-Oilers-small.png",
    stjernen: "/team-logos/Stjernen-small.png",
    storhamar: "/team-logos/Storhamar-small.png",
    vålerenga: "/team-logos/Valerenga-small.png",
    comet: "/team-logos/Comet-small.png",
};

const TEAM_LOGOS_LIGHT: Record<string, string> = {
    narvik: "/team-logos/Narvik-Hockey-small.png",
};

export function formatNumber(value: number, decimals = 2): string {
    if (value === 0) return "-";
    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
}

/**
 * Parses entity reference tokens in strings.
 * Handles two formats:
 *   type:{id|displayName}  →  displayName
 *   type:{id}              →  TypeLabel #id  (fallback when no display name)
 */
export function parseEntityRefs(text: string): string {
    if (!text) return text;
    // Format with display name: match:{123|Home vs Away}
    let result = text.replace(/(\w+):\{(\d+)\|([^}]+)\}/g, (_full, _type, _id, displayName) => displayName);
    // Format without display name: match:{123}
    const typeLabels: Record<string, string> = {
        match: "Match",
        player: "Player",
        tournament: "Tournament",
    };
    result = result.replace(/(\w+):\{(\d+)\}/g, (_full, type, id) => {
        const label = typeLabels[type.toLowerCase()] ?? type;
        return `${label} #${id}`;
    });
    return result;
}

export function getTeamLogo(teamName?: string, variant: "dark" | "light" = "dark"): string | undefined {
    if (!teamName) return undefined;
    const lower = teamName.toLowerCase();
    if (variant === "light") {
        const lightLogo = Object.entries(TEAM_LOGOS_LIGHT).find(([key]) => lower.includes(key))?.[1];
        if (lightLogo) return lightLogo;
    }
    return Object.entries(TEAM_LOGOS).find(([key]) => lower.includes(key))?.[1];
}
